/**
 * Readable Search JSON — the human/AI-friendly representation of a search, and
 * the bidirectional bridge to the flat, string-encoded filter store
 * (`SearchBrowseParams = Record<string, string>`).
 *
 * See docs/copy-apply-search-payload.md. The store keeps URL-era delimiter
 * strings (`^`, `::`, `>`, `:`); this module presents them as structured JSON
 * and parses edited/pasted JSON back into the store. Round-trip safety is
 * guaranteed by the present layer, which only emits a structured value when it
 * re-encodes byte-for-byte to the original string (see `present.ts`).
 */

/** How a given store field's value is decoded to / encoded from readable JSON. */
export enum CodecKind {
  /** `a^b^c` ⇄ `["a","b","c"]` */
  LIST = 'LIST',
  /** `min>max` (either side optional) ⇄ `{ min?, max? }` */
  RANGE = 'RANGE',
  /** `y:m>y:m` ⇄ `{ from: {years?,months?}, to: {years?,months?} }` */
  DURATION = 'DURATION',
  /** `name::id(::extra)^…` ⇄ `[{ name, id?, extra? }]` */
  ENTITY = 'ENTITY',
  /** `a::b::c^…` ⇄ `[["a","b","c"], …]` (location / department paths) */
  TREE = 'TREE',
  /** `field:MODE^field:MODE` ⇄ `{ field: MODE }` (exact_match / *_search_match) */
  MATCH_MAP = 'MATCH_MAP',
  /** `"true"|"false"` ⇄ `true|false` */
  BOOLEAN = 'BOOLEAN',
  /** single scalar string, passed through unchanged */
  SINGLE = 'SINGLE',
  /** `depts|min>max[|type];…` ⇄ `[{ departments, min?, max?, type? }]` */
  EMPLOYEE_BY_DEPARTMENT = 'EMPLOYEE_BY_DEPARTMENT',
  /** `depts|min>max|timeFrame[|type];…` ⇄ `[{ departments, min?, max?, timeFrame, type? }]` */
  HEADCOUNT_GROWTH = 'HEADCOUNT_GROWTH',
  /** keyword source codes ⇄ readable source labels (`["Company Name", …]`) */
  KEYWORD_SOURCES = 'KEYWORD_SOURCES',
  /** `SOURCE:MODE^…` ⇄ `{ "Company Name": "Smart", … }` (readable labels) */
  KEYWORD_MATCH = 'KEYWORD_MATCH',
}

/**
 * Curated readable names for a few keys whose camelCased store key is still
 * cryptic. Everything else uses the bijective snake_case⇄camelCase transform
 * (see keys.ts). Keep entries here bijective (no two store keys map to the same
 * readable key) — a dev guard in keys.ts enforces it.
 */
export const KEY_OVERRIDES: Record<string, string> = {
  current_job_experience_current_job: 'timeInCurrentRole',
  current_job_experience_current_company: 'timeAtCurrentCompany',
  current_job_experience_total: 'totalYearsOfExperience',
};

/** `modality` uses backend enum values; expose them lower-cased and readable. */
export const MODALITY_READABLE: Record<string, string> = {
  TOTAL: 'total',
  UNSAVED: 'netNew',
  SAVED: 'saved',
};

/** UUID matcher used to anchor 3-part `name::id::extra` entity decoding. */
export const UUID_PATTERN =
  /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi;
